import { Search } from '@mui/icons-material'
import { Box, InputAdornment, Link, Stack, TextField, Typography, styled } from '@mui/material'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import ToggleGroupButtons from 'src/components/common/ToggleGroupButtons'
import type { BillingCustomer, CustomerBillingTab } from 'src/generated-types/customer-license'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { CustomerBillingCard } from './CustomerBillingCard'

type CustomerSummarySidebarProps = {
  tab: CustomerBillingTab
  onTabChange: (tab: CustomerBillingTab) => void
  search: string
  onSearchChange: (v: string) => void
  customersList: BillingCustomer[]
  billingPeriodFrom: Date | null
  billingPeriodTo: Date | null
  selectedCustomerId: string | null
  onSelectCustomer: (id: string) => void
  onEditCustomer: (customer: BillingCustomer) => void
  onClearSearch: () => void
}

export const CustomerSummarySidebar = ({
  tab,
  onTabChange,
  search,
  onSearchChange,
  customersList,
  billingPeriodFrom,
  billingPeriodTo,
  selectedCustomerId,
  onSelectCustomer,
  onEditCustomer,
  onClearSearch,
}: CustomerSummarySidebarProps) => {
  const { t } = useTranslation('admin-license')
  const count = customersList.length
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const customerId = searchParams.get('customer')
    if (!customerId) return
    onSelectCustomer(customerId)
    const scrollToCard = () => {
      const mainElement = document.getElementById('customer-list-billing')
      mainElement?.scrollTo({
        top: document.getElementById(`customer-${customerId}`)?.offsetTop,
        behavior: 'smooth',
      })
    }
    requestAnimationFrame(() => requestAnimationFrame(scrollToCard))
  }, [onSelectCustomer, searchParams])

  const handleSelectCustomer = (customerId: string) => {
    onSelectCustomer(customerId)
    setSearchParams({ customer: customerId })
  }

  return (
    <CustomerSummarySidebarRoot>
      <Typography variant="subtitle1" fontWeight={600} color="text.primary">
        {t('customerSummary')}
      </Typography>
      <Box sx={{ width: '100%' }}>
        <ToggleGroupButtons
          actions={[
            { title: t('active'), value: 'active' as CustomerBillingTab },
            { title: t('inactive'), value: 'inactive' as CustomerBillingTab },
          ]}
          defaultValue="active"
          buttonWidth="100%"
          style={{ width: '100% ' }}
          value={tab}
          setViewMode={v => onTabChange(v as CustomerBillingTab)}
          height="36px"
          fullWidth
        />
      </Box>

      <TextField
        size="small"
        placeholder={t('searchCustomers')}
        value={search}
        onChange={e => onSearchChange(e.target.value)}
        inputProps={{ 'aria-label': t('searchCustomers') }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" color="action" aria-hidden />
            </InputAdornment>
          ),
        }}
        fullWidth
      />
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="caption" fontWeight={600} letterSpacing={0.5}>
          {tab === 'active' ? t('customersCountActive', { count }) : t('customersCountInactive', { count })}
        </Typography>
        <Link
          component="button"
          type="button"
          variant="caption"
          underline="hover"
          onClick={onClearSearch}
          sx={{ fontWeight: 600 }}
        >
          {t('clear')}
        </Link>
      </Stack>
      <Stack
        id="customer-list-billing"
        component="nav"
        aria-label={String(t('customerListNavAria'))}
        spacing={1}
        sx={{ overflow: 'auto', maxHeight: { md: 'calc(100vh - 320px)' } }}
      >
        {customersList.map(c => (
          <CustomerBillingCard
            key={c.id}
            customer={c}
            billingPeriodFrom={billingPeriodFrom}
            billingPeriodTo={billingPeriodTo}
            selected={selectedCustomerId === c.id}
            onSelect={() => handleSelectCustomer(c.id)}
            onEdit={() => onEditCustomer(c)}
          />
        ))}
      </Stack>
    </CustomerSummarySidebarRoot>
  )
}

const CustomerSummarySidebarRoot = styled(Box)(({ theme }) => {
  const divider = getThemeColor(theme, MUIThemeColors.secondaryLight)
  return {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    paddingTop: 0,
    borderBottom: `1px solid ${divider}`,
    [theme.breakpoints.up('md')]: {
      width: '28%',
      minWidth: 260,
      maxWidth: 360,
      borderRight: `1px solid ${divider}`,
      borderBottom: 'none',
    },
  }
})
